import HomeIcon from '@ingka/ssr-icon/paths/home';
import CreateNewIcon from '@ingka/ssr-icon/paths/create-new';
import ListIcon from '@ingka/ssr-icon/paths/list';
import IconFeedback1 from '@ingka/ssr-icon/paths/feedback-sad';
import IconFeedback2 from '@ingka/ssr-icon/paths/feedback-dissatisfied';
import IconFeedback3 from '@ingka/ssr-icon/paths/feedback-neutral';
import IconFeedback4 from '@ingka/ssr-icon/paths/feedback-satisfied';
import IconFeedback5 from '@ingka/ssr-icon/paths/feedback-happy';

import type {
  CarrierSize,
  CarrierType,
  CommunicationPurpose,
  Country,
  CtaScore,
  PermissionLevel,
  Route,
  StoreComponent,
} from '@/typings/constants';

export const SUPPORTED_LOCALES: string[] = [
  'en-GB', // English
  'es-ES', // Spanish
  'sv-SE', // Swedish
  'fr-FR', // French
  'ja-JP', // Japanese
  'it-IT', // Italian
  'nl-NL', // Dutch
  'de-DE', // German
  'ko-KR', // Korean
  'pt-PT', // Portuguese
  'no-NO', // Norwegian
  'cs-CZ', // Czech
  'pl-PL', // Polish
  'da-DK', // Danish
  'ro-RO', // Romanian
  'hr-HR', // Croatian
  'sr-RS', // Serbian
  'sl-SI', // Slovenian
  'hu-HU', // Hungarian
  'sk-SK', // Slovak
  'fi-FI', // Finnish
];

export const CTA_CONFIRMATION_TIMEOUT = 10 * 1000; // 10 seconds
export const CTA_SCORES: CtaScore[] = [
  { score: 1, icon: IconFeedback1, label: 'label.veryDissatisfied' },
  { score: 2, icon: IconFeedback2, label: 'label.dissatisfied' },
  { score: 3, icon: IconFeedback3, label: 'label.indiferent' },
  { score: 4, icon: IconFeedback4, label: 'label.satisfied' },
  { score: 5, icon: IconFeedback5, label: 'label.verySatisfied' },
];
export const CTA_MAX_SCORE = CTA_SCORES.reduce(
  (acc, { score }) => Math.max(acc, score),
  0
);

export const TOAST_DURATION_PER_CHAR = 50;
export const MAX_TOAST_DURATION = 10000;

export const ROUTES: Route[] = [
  {
    className: 'tutorial__onboarding--step3',
    translationKey: 'routes.home',
    path: '/',
    icon: HomeIcon,
  },
  {
    className: 'tutorial__onboarding--step4',
    translationKey: 'routes.create-code',
    path: '/create-code',
    icon: CreateNewIcon,
  },
  {
    className: 'tutorial__onboarding--step5',
    translationKey: 'routes.codes',
    path: '/codes',
    icon: ListIcon,
  },
];

export const COUNTRIES: Country[] = [
  {
    value: 'australia',
    text: 'Australia',
    code: 'au',
  },
  {
    value: 'austria',
    text: 'Austria',
    code: 'at',
  },
  {
    value: 'belgium',
    text: 'Belgium',
    code: 'be',
  },
  {
    value: 'canada',
    text: 'Canada',
    code: 'ca',
  },
  {
    value: 'china',
    text: 'China',
    code: 'cn',
  },
  {
    value: 'czech republic',
    text: 'Czech Republic',
    code: 'cz',
  },
  {
    value: 'croatia',
    text: 'Croatia',
    code: 'hr',
  },
  {
    value: 'denmark',
    text: 'Denmark',
    code: 'dk',
  },
  {
    value: 'estonia',
    text: 'Estonia',
    code: 'ee',
  },
  {
    value: 'finland',
    text: 'Finland',
    code: 'fi',
  },
  {
    value: 'france',
    text: 'France',
    code: 'fr',
  },
  {
    value: 'germany',
    text: 'Germany',
    code: 'de',
  },
  {
    value: 'hungary',
    text: 'Hungary',
    code: 'hu',
  },
  {
    value: 'india',
    text: 'India',
    code: 'in',
  },
  {
    value: 'ireland',
    text: 'Ireland',
    code: 'ie',
  },
  {
    value: 'italy',
    text: 'Italy',
    code: 'it',
  },
  {
    value: 'japan',
    text: 'Japan',
    code: 'jp',
  },
  {
    value: 'south korea',
    text: 'South Korea',
    code: 'kr',
  },
  {
    value: 'latvia',
    text: 'Latvia',
    code: 'lv',
  },
  {
    value: 'lithuania',
    text: 'Lithuania',
    code: 'lt',
  },
  {
    value: 'netherlands',
    text: 'Netherlands',
    code: 'nl',
  },
  {
    value: 'new zealand',
    text: 'New Zealand',
    code: 'nz',
  },
  {
    value: 'norway',
    text: 'Norway',
    code: 'no',
  },
  {
    value: 'poland',
    text: 'Poland',
    code: 'pl',
  },
  {
    value: 'portugal',
    text: 'Portugal',
    code: 'pt',
  },
  {
    value: 'romania',
    text: 'Romania',
    code: 'ro',
  },
  {
    value: 'russian federation',
    text: 'Russian Federation',
    code: 'ru',
  },
  {
    value: 'serbia',
    text: 'Serbia',
    code: 'rs',
  },
  {
    value: 'slovakia',
    text: 'Slovakia',
    code: 'sk',
  },
  {
    value: 'slovenia',
    text: 'Slovenia',
    code: 'si',
  },
  {
    value: 'spain',
    text: 'Spain',
    code: 'es',
  },
  {
    value: 'sweden',
    text: 'Sweden',
    code: 'se',
  },
  {
    value: 'switzerland',
    text: 'Switzerland',
    code: 'ch',
  },
  {
    value: 'ukraine',
    text: 'Ukraine',
    code: 'ua',
  },
  {
    value: 'united kingdom',
    text: 'United Kingdom',
    code: 'gb',
  },
  {
    value: 'united states',
    text: 'United States',
    code: 'us',
  },
];

export const STORE_COMPONENTS: StoreComponent[] = [
  {
    value: 'parkingAndTraffic',
    departments: [],
  },
  {
    value: 'entrance',
    departments: [],
  },
  {
    value: 'showroom',
    departments: [
      { value: 'showRoomBathroom' },
      { value: 'bedroom' },
      { value: 'childrensIkea' },
      { value: 'dining' },
      { value: 'kitchen' },
      { value: 'livingRoom' },
      { value: 'livingRoomStorage' },
      { value: 'workspaces' },
    ],
  },
  {
    value: 'swedishRestaurant',
    departments: [],
  },
  {
    value: 'marketHall',
    departments: [
      { value: 'marketHallBathroom' },
      { value: 'bedTextiles' },
      { value: 'cookwareAndTableware' },
      { value: 'homeDecoration' },
      { value: 'homeOrganization' },
      { value: 'lightingAndHomeElectronics' },
      { value: 'rugsAndFlooring' },
      { value: 'textiles' },
      { value: 'wallDecorationsAndMirrors' },
    ],
  },
  {
    value: 'selfServeFurnitureArea',
    departments: [],
  },
  {
    value: 'warehouse',
    departments: [],
  },
  {
    value: 'goodsReceiving',
    departments: [],
  },
  {
    value: 'checkOutCashOffice',
    departments: [],
  },
  {
    value: 'customerService',
    departments: [],
  },
  {
    value: 'exit',
    departments: [],
  },
  {
    value: 'officeCoWorkerSpaces',
    departments: [],
  },
  {
    value: 'mechanicalTechnical',
    departments: [],
  },
  {
    value: 'swedishBistro',
    departments: [],
  },
  {
    value: 'swedishFoodMarket',
    departments: [],
  },
  {
    value: 'swedishCafe',
    departments: [],
  },
  {
    value: 'swedishDeli',
    departments: [],
  },
  {
    value: 'swedishSnack',
    departments: [],
  },
  {
    value: 'swedishBite',
    departments: [],
  },
];

export const CARRIER_TYPES: CarrierType[] = [
  { value: 'digital' },
  { value: 'table' },
  { value: 'video' },
  { value: 'poster' },
  { value: 'totem' },
  { value: 'info' },
  { value: 'activity' },
];

export const CARRIER_SIZES: CarrierSize[] = [
  { value: 'na' },
  { value: 'a2' },
  { value: 'a3' },
  { value: 'a4' },
  { value: 'a5Folded' },
  { value: 'a6' },
  { value: 'a7' },
  { value: '50x70cm' },
];

export const COMMUNICATION_PURPOSES: CommunicationPurpose[] = [
  {
    value: 'product',
    i18nKey: 'label.communicationPurposes.product',
    tooltipi18nKey: 'label.communicationPurposes.tooltips.product',
    subcategories: [
      {
        value: 'width',
        i18nKey: 'label.communicationPurposes.subcategories.width',
        icons: ['information-circle'],
      },
      {
        value: 'richContent',
        i18nKey: 'label.communicationPurposes.subcategories.richContent',
        icons: ['plus-circle'],
      },
      {
        value: 'nextStep',
        i18nKey: 'label.communicationPurposes.subcategories.nextStep',
        icons: ['arrow-right-to-line-small'],
      },
      {
        value: 'narrowing',
        i18nKey: 'label.communicationPurposes.subcategories.narrowing',
        icons: ['information-circle'],
      },
    ],
  },
  {
    value: 'tools',
    i18nKey: 'label.communicationPurposes.tools',
    tooltipi18nKey: 'label.communicationPurposes.tooltips.tools',
    subcategories: [
      {
        value: 'downloadApp',
        i18nKey: 'label.communicationPurposes.subcategories.downloadApp',
        icons: ['arrow-cloud-out'],
      },
      {
        value: 'storeNavigation',
        i18nKey: 'label.communicationPurposes.subcategories.storeNavigation',
        icons: ['mobile_with_location_pointer'],
      },
      {
        value: 'waysToShop',
        i18nKey: 'label.communicationPurposes.subcategories.waysToShop',
        icons: ['shopping-trolley'],
      },
      {
        value: 'scanGo',
        i18nKey: 'label.communicationPurposes.subcategories.scanGo',
        icons: ['scan-barcode'],
      },
      {
        value: 'designTools',
        i18nKey: 'label.communicationPurposes.subcategories.designTools',
        icons: ['laptop-pencil'],
      },
      {
        value: 'foodMobile',
        i18nKey: 'label.communicationPurposes.subcategories.foodMobile',
        icons: ['arrow-cloud-out'],
      },
    ],
  },
  {
    value: 'book',
    i18nKey: 'label.communicationPurposes.book',
    tooltipi18nKey: 'label.communicationPurposes.tooltips.book',
    subcategories: [
      {
        value: 'loyaltyProgramme',
        i18nKey: 'label.communicationPurposes.subcategories.loyaltyProgramme',
        icons: ['membership-card'],
      },
      {
        value: 'appointmentRegistration',
        i18nKey:
          'label.communicationPurposes.subcategories.appointmentRegistration',
        icons: ['calendar'],
      },
      {
        value: 'queuingRegistration',
        i18nKey:
          'label.communicationPurposes.subcategories.queuingRegistration',
        icons: ['persons-queuing'],
      },
      {
        value: 'bookEvents',
        i18nKey: 'label.communicationPurposes.subcategories.bookEvents',
        icons: ['calendar'],
      },
    ],
  },
  {
    value: 'feedback',
    i18nKey: 'label.communicationPurposes.feedback',
    tooltipi18nKey: 'label.communicationPurposes.tooltips.feedback',
    subcategories: [
      {
        value: 'opinionsFeedback',
        i18nKey: 'label.communicationPurposes.subcategories.opinionsFeedback',
        icons: ['feedback-happy'],
      },
      {
        value: 'productRatings',
        i18nKey: 'label.communicationPurposes.subcategories.productRatings',
        icons: ['star'],
      },
      {
        value: 'shoppingExperience',
        i18nKey: 'label.communicationPurposes.subcategories.shoppingExperience',
        icons: ['person-with-shopping-bag'],
      },
    ],
  },
];

export const FREE_TEXT_COMMUNICATION_PURPOSES: CommunicationPurpose[] = [
  {
    value: 'sellMore',
    i18nKey: 'label.freeTextCommunicationPurposes.sellMore',
    tooltipi18nKey: '',
    subcategories: [
      {
        value: 'visitation',
        i18nKey: 'label.freeTextCommunicationPurposes.subcategories.visitation',
        icons: [''],
      },
      {
        value: 'conversion',
        i18nKey: 'label.freeTextCommunicationPurposes.subcategories.conversion',
        icons: [''],
      },
      {
        value: 'averageTicket',
        i18nKey:
          'label.freeTextCommunicationPurposes.subcategories.averageTicket',
        icons: [''],
      },
      {
        value: 'grossMargin',
        i18nKey:
          'label.freeTextCommunicationPurposes.subcategories.grossMargin',
        icons: [''],
      },
      {
        value: 'quantities',
        i18nKey: 'label.freeTextCommunicationPurposes.subcategories.quantities',
        icons: [''],
      },
      {
        value: 'circularRevenue',
        i18nKey:
          'label.freeTextCommunicationPurposes.subcategories.circularRevenue',
        icons: [''],
      },
    ],
  },
  {
    value: 'saveCost',
    i18nKey: 'label.freeTextCommunicationPurposes.saveCost',
    tooltipi18nKey: '',
    subcategories: [
      {
        value: 'staffCost',
        i18nKey: 'label.freeTextCommunicationPurposes.subcategories.staffCost',
        icons: [''],
      },
      {
        value: 'equipment',
        i18nKey: 'label.freeTextCommunicationPurposes.subcategories.equipment',
        icons: [''],
      },
      {
        value: 'otherOperationalCost',
        i18nKey:
          'label.freeTextCommunicationPurposes.subcategories.otherOperationalCost',
        icons: [''],
      },
      {
        value: 'transactionTypes',
        i18nKey:
          'label.freeTextCommunicationPurposes.subcategories.transactionTypes',
        icons: [''],
      },
      {
        value: 'energy',
        i18nKey: 'label.freeTextCommunicationPurposes.subcategories.energy',
        icons: [''],
      },
    ],
  },
  {
    value: 'efficiency',
    i18nKey: 'label.freeTextCommunicationPurposes.efficiency',
    tooltipi18nKey: '',
    subcategories: [
      {
        value: 'goodsHandling',
        i18nKey:
          'label.freeTextCommunicationPurposes.subcategories.goodsHandling',
        icons: [''],
      },
      {
        value: 'fulfillment',
        i18nKey:
          'label.freeTextCommunicationPurposes.subcategories.fulfillment',
        icons: [''],
      },
      {
        value: 'increasedPerformance',
        i18nKey:
          'label.freeTextCommunicationPurposes.subcategories.increasedPerformance',
        icons: [''],
      },
      {
        value: 'reducedOperationalExpenses',
        i18nKey:
          'label.freeTextCommunicationPurposes.subcategories.reducedOperationalExpenses',
        icons: [''],
      },
    ],
  },
  {
    value: 'customer',
    i18nKey: 'label.freeTextCommunicationPurposes.customer',
    tooltipi18nKey: '',
    subcategories: [
      {
        value: 'eatingTime',
        i18nKey: 'label.freeTextCommunicationPurposes.subcategories.eatingTime',
        icons: [''],
      },
      {
        value: 'ikeaFamilyNewMembers',
        i18nKey:
          'label.freeTextCommunicationPurposes.subcategories.ikeaFamilyNewMembers',
        icons: [''],
      },
      {
        value: 'ikeaFamilyMemberReach',
        i18nKey:
          'label.freeTextCommunicationPurposes.subcategories.ikeaFamilyMemberReach',
        icons: [''],
      },
      {
        value: 'satisfaction',
        i18nKey:
          'label.freeTextCommunicationPurposes.subcategories.satisfaction',
        icons: [''],
      },
      {
        value: 'services',
        i18nKey: 'label.freeTextCommunicationPurposes.subcategories.services',
        icons: [''],
      },
    ],
  },
  {
    value: 'peopleCulture',
    i18nKey: 'label.freeTextCommunicationPurposes.peopleCulture',
    tooltipi18nKey: '',
    subcategories: [
      {
        value: 'retention',
        i18nKey: 'label.freeTextCommunicationPurposes.subcategories.retention',
        icons: [''],
      },
      {
        value: 'safetySecurity',
        i18nKey:
          'label.freeTextCommunicationPurposes.subcategories.safetySecurity',
        icons: [''],
      },
      {
        value: 'engagement',
        i18nKey: 'label.freeTextCommunicationPurposes.subcategories.engagement',
        icons: [''],
      },
    ],
  },
  {
    value: 'peoplePlanet',
    i18nKey: 'label.freeTextCommunicationPurposes.peoplePlanet',
    tooltipi18nKey: '',
    subcategories: [
      {
        value: 'waste',
        i18nKey: 'label.freeTextCommunicationPurposes.subcategories.waste',
        icons: [''],
      },
      {
        value: 'waterUsage',
        i18nKey: 'label.freeTextCommunicationPurposes.subcategories.waterUsage',
        icons: [''],
      },
      {
        value: 'socialImpact',
        i18nKey:
          'label.freeTextCommunicationPurposes.subcategories.socialImpact',
        icons: [''],
      },
      {
        value: 'changeMakers',
        i18nKey:
          'label.freeTextCommunicationPurposes.subcategories.changeMakers',
        icons: [''],
      },
      {
        value: 'climateFootprint',
        i18nKey:
          'label.freeTextCommunicationPurposes.subcategories.climateFootprint',
        icons: [''],
      },
    ],
  },
  {
    value: 'ikeaFood',
    i18nKey: 'label.freeTextCommunicationPurposes.ikeaFood',
    tooltipi18nKey: '',
    subcategories: [
      {
        value: 'sellMore',
        i18nKey:
          'label.freeTextCommunicationPurposes.subcategories.foodSellMore',
        icons: [''],
      },
      {
        value: 'takeUpRate',
        i18nKey: 'label.freeTextCommunicationPurposes.subcategories.takeUpRate',
        icons: [''],
      },
      {
        value: 'saveCost',
        i18nKey:
          'label.freeTextCommunicationPurposes.subcategories.foodSaveCost',
        icons: [''],
      },
      {
        value: 'efficiency',
        i18nKey:
          'label.freeTextCommunicationPurposes.subcategories.foodEfficiency',
        icons: [''],
      },
      {
        value: 'happyCustomer',
        i18nKey:
          'label.freeTextCommunicationPurposes.subcategories.happyCustomer',
        icons: [''],
      },
      {
        value: 'energySaving',
        i18nKey:
          'label.freeTextCommunicationPurposes.subcategories.energySaving',
        icons: [''],
      },
      {
        value: 'plantBased',
        i18nKey: 'label.freeTextCommunicationPurposes.subcategories.plantBased',
        icons: [''],
      },
      {
        value: 'foodWaste',
        i18nKey: 'label.freeTextCommunicationPurposes.subcategories.foodWaste',
        icons: [''],
      },
    ],
  },
];

export const PERMISSION_LEVELS: PermissionLevel[] = [
  {
    value: 'private',
    i18nKey: 'label.permissions.private.name',
  },
  {
    value: 'location',
    i18nKey: 'label.permissions.location.name',
  },
  {
    value: 'public',
    i18nKey: 'label.permissions.public.name',
  },
];
